import React from "react";
import { ImageGeneratorHistoryRecord } from "../../app/api/imageGenerator";
import { CollectionBuildSessionSummary } from "../../app/api/collectionBuild";
import { collectionBuildSummaryTitle } from "./collectionBuildSession";
import {
  imageGeneratorRootListStatus,
  imageGeneratorSessionTitle,
} from "./imageGeneratorSession";

export type GenerativeRecentItem = {
  key: string;
  kind: "image" | "collection";
  id: number;
  sessionId?: number;
  title: string;
  status: string;
  createdAt: string;
};

type Props = {
  items: GenerativeRecentItem[];
  activeKey: string | null;
  collapsed: boolean;
  onToggle: () => void;
  onSelect: (item: GenerativeRecentItem) => void;
};

export const buildGenerativeRecentItems = (
  imageSessions: ImageGeneratorHistoryRecord[]
): GenerativeRecentItem[] => {
  const imageRoots = imageSessions.filter(item => !item.sourceGenerationId);
  return imageRoots
    .map(record => ({
      key: `image-${record.id}`,
      kind: "image" as const,
      id: record.id,
      title: imageGeneratorSessionTitle(record),
      status: imageGeneratorRootListStatus(record, imageSessions),
      createdAt: record.createdAt,
    }))
    .sort(
      (left, right) =>
        new Date(right.createdAt).getTime() - new Date(left.createdAt).getTime()
    );
};

export const buildCollectionRecentItems = (
  summaries: CollectionBuildSessionSummary[]
): GenerativeRecentItem[] =>
  summaries.map(item => ({
    key: `collection-${item.sessionId}`,
    kind: "collection",
    id: item.sessionId,
    sessionId: item.sessionId,
    title: collectionBuildSummaryTitle(item),
    status: item.status,
    createdAt: item.updatedAt || item.createdAt,
  }));

const statusLabel = (item: GenerativeRecentItem) => {
  if (item.status === "generating") {
    return "Generating…";
  }
  if (item.status === "failed") {
    return "Failed";
  }
  const created = new Date(item.createdAt);
  if (Number.isNaN(created.getTime())) {
    return "Ready";
  }
  return created.toLocaleString([], {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
};

const ImageGeneratorRecents: React.FC<Props> = ({
  items,
  activeKey,
  collapsed,
  onToggle,
  onSelect,
}) => (
  <div className="mt-2 px-1">
    <button
      type="button"
      onClick={onToggle}
      className="flex w-full items-center justify-between rounded-xl px-2 py-2 text-left text-secondary hover:bg-gray"
      aria-expanded={!collapsed}
    >
      <span className="text-sm font-medium">Recents</span>
      <svg
        width="14"
        height="14"
        viewBox="0 0 24 24"
        fill="none"
        className={`shrink-0 transition-transform ${
          collapsed ? "-rotate-90" : ""
        }`}
        aria-hidden="true"
      >
        <path
          d="M6 9l6 6 6-6"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </button>
    {!collapsed && (
      <div className="mt-1 max-h-[40vh] overflow-y-auto">
        {items.length === 0 ? (
          <p className="px-2 py-2 text-xs text-tertiary">
            Recent collections and looks will show up here.
          </p>
        ) : (
          <ul className="flex flex-col gap-0.5">
            {items.map(item => {
              const active = item.key === activeKey;
              return (
                <li key={item.key}>
                  <button
                    type="button"
                    onClick={() => onSelect(item)}
                    className={`w-full rounded-xl px-3 py-2 text-left transition-colors ${
                      active
                        ? "bg-[#F3EFEA] text-black"
                        : "text-secondary hover:bg-gray"
                    }`}
                  >
                    <span className="block truncate text-xs font-medium">
                      {item.title}
                    </span>
                    <span
                      className={`mt-0.5 block truncate text-[11px] ${
                        item.status === "failed"
                          ? "text-red-600"
                          : "text-secondary"
                      }`}
                    >
                      {statusLabel(item)}
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    )}
  </div>
);

export default ImageGeneratorRecents;
